import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  output,
  signal,
  viewChild,
  ViewContainerRef,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { BreakpointObserver } from '@angular/cdk/layout';
import { map, of } from 'rxjs';
import { CommonModule } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { BREAKPOINTS_PX } from '@vendasta/galaxy/constants';
import { GalaxyLoadingSpinnerModule } from '@vendasta/galaxy/loading-spinner';
import { GalaxyPipesModule } from '@vendasta/galaxy/pipes';
import { TranslateModule } from '@ngx-translate/core';
import { MatButtonToggle, MatButtonToggleGroup } from '@angular/material/button-toggle';
import { MatIcon } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { InboxPreviewComponent } from '../inbox-preview/inbox-preview.component';
import { Contact, ContactProvider, CONTENT_BUILDER_DEPENDENCIES } from '../interface';
import { ContactSelectorModalComponent } from '../../../shared/src/contact-selector-modal/contact-selector-modal.component';
import { MatTooltip } from '@angular/material/tooltip';

type DeviceType = 'desktop' | 'tablet' | 'mobile';

@Component({
  selector: 'email-builder-preview',
  templateUrl: './preview.component.html',
  styleUrls: ['./preview.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    GalaxyLoadingSpinnerModule,
    GalaxyPipesModule,
    MatButtonToggle,
    MatButtonToggleGroup,
    MatIcon,
    TranslateModule,
    MatButtonModule,
    MatTooltip,
    InboxPreviewComponent,
  ],
})
export class PreviewComponent {
  private readonly breakpointObserver = inject(BreakpointObserver);
  private readonly dialog = inject(MatDialog);
  private readonly dependencies = inject(CONTENT_BUILDER_DEPENDENCIES, { optional: true });
  private readonly viewContainerRef = inject(ViewContainerRef);

  readonly renderedHtml = input<string>('');
  readonly subject = input<string>('');
  readonly previewText = input<string>('');
  readonly senderName = input('');
  readonly saveDisabled = input(false);
  readonly contactProvider = input<ContactProvider | undefined>(undefined);
  readonly showActions = input(true);
  readonly saveClicked = output<void>();
  readonly sendPreviewClicked = output<void>();
  readonly contactSelected = output<Contact | null>();

  protected readonly hasContactProvider = computed(() => !!this.contactProvider());
  protected readonly showSendPreview =  this.dependencies?.sendPreviewService;
  private readonly previewDisabled$ =
    this.dependencies?.sendPreviewService?.canSendPreview$?.pipe(map((canSend) => !canSend)) ?? of(false);
  protected readonly previewDisabled = toSignal(this.previewDisabled$, { initialValue: false });

  // Breakpoint detection
  protected readonly isMobile = toSignal(
    this.breakpointObserver
      .observe([`(max-width: ${BREAKPOINTS_PX.TABLET_MINIMUM})`])
      .pipe(map((result) => result.matches)),
    { initialValue: false },
  );

  // Device selection state
  protected readonly selectedDevice = signal<DeviceType>('desktop');
  protected readonly showDesktopButton = signal<boolean>(true);

  // Contact selection state
  protected readonly selectedContact = signal<Contact | null>(null);

  // Iframe reference
  protected readonly iframePreview = viewChild<ElementRef>('iframePreview');

  // Computed signal for email width based on selected device
  protected readonly emailPreviewWidth = computed(() => {
    switch (this.selectedDevice()) {
      case 'desktop':
        return '100%';
      case 'tablet':
        return '600px';
      case 'mobile':
        return '320px';
      default:
        return '100%';
    }
  });

  protected readonly isMobilePreview = computed(() => this.selectedDevice() === 'mobile');

  constructor() {
    // Effect to update iframe when HTML changes
    effect(() => {
      const html = this.renderedHtml();
      const iframe = this.iframePreview();

      // Only update if HTML is present and iframe exists
      if (html && iframe && !this.isMobile()) {
        // Small delay to ensure iframe is ready
        setTimeout(() => this.updateIFramePreview(html), 0);
      }
    });
  }

  protected previewSizeClicked(device: DeviceType): void {
    this.selectedDevice.set(device);
  }

  protected onSaveClick(): void {
    this.saveClicked.emit();
  }

  protected onSendPreviewClick(): void {
    this.sendPreviewClicked.emit();
  }

  protected onSelectContactClick(): void {
    const provider = this.contactProvider();
    if (!provider) {
      return;
    }

    this.dialog
      .open(ContactSelectorModalComponent, {
        viewContainerRef: this.viewContainerRef,
        width: '500px',
        minHeight: '496px',
        data: provider,
      })
      .afterClosed()
      .subscribe((contact: Contact | null) => {
        if (contact) {
          this.selectedContact.set(contact);
          this.contactSelected.emit(contact);
        }
      });
  }

  protected updateIFramePreview(html: string): void {
    if (this.isMobile() || !html) {
      return;
    }

    const iframe = this.iframePreview();
    if (!iframe) {
      return;
    }

    const contentWindow = iframe.nativeElement.contentWindow;
    const contentDocument = iframe.nativeElement.contentDocument;

    if (!contentWindow || !contentDocument) {
      return;
    }

    const scrollX = contentWindow.scrollX;
    const scrollY = contentWindow.scrollY;

    contentDocument.open();
    contentDocument.write(html);
    contentDocument.close();

    // Firefox doesn't preserve scroll, so set it manually
    iframe.nativeElement.contentWindow.scrollTo(scrollX, scrollY);
  }

  protected setIframeHeightOnMobile(): void {
    if (!this.isMobile()) {
      return;
    }

    const iframe = this.iframePreview();
    if (!iframe) {
      return;
    }

    const iframeBody = iframe.nativeElement.contentWindow?.document?.body;
    if (iframeBody) {
      iframe.nativeElement.style.height = iframeBody.offsetHeight + 40 + 'px';
    }
  }
}
